import React, { useState } from "react";
import api from "../lib/api";
import { Send, CheckCircle2 } from "lucide-react";

const initial = {
  company_name: "", contact_name: "", email: "", phone: "",
  country: "", port_of_discharge: "",
  product_interest: "saffron", estimated_quantity_kg: "",
  target_price_usd_kg: "", incoterm: "FOB", message: "",
};

export default function Wholesale() {
  const [form, setForm] = useState(initial);
  const [submitted, setSubmitted] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm({ ...form, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      const payload = {
        ...form,
        estimated_quantity_kg: parseFloat(form.estimated_quantity_kg),
        target_price_usd_kg: form.target_price_usd_kg ? parseFloat(form.target_price_usd_kg) : null,
      };
      await api.post("/inquiries", payload);
      setSubmitted(true);
      setForm(initial);
    } catch (e) {
      setErr(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || "Could not submit. Please try again.");
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="wholesale-page" className="pt-28 pb-24">
      <section className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5 md:sticky md:top-28 self-start">
          <p className="text-xs uppercase tracking-overline text-[#4A5D23] mb-4">B2B / Bulk Export</p>
          <h1 className="font-serif-display text-6xl md:text-7xl tracking-tighter leading-[0.95]">
            Order a<br/>container.<br/>
            <em className="text-[#C84B31] not-italic">By the kilo.</em>
          </h1>
          <p className="mt-8 text-[#4A4A4A] leading-relaxed text-lg">
            Tell us what you need &mdash; quantity, port and timeline. We&apos;ll respond within 24 hours
            with FOB or CIF pricing, lab reports and packaging specs.
          </p>
          <ul className="mt-8 space-y-2 text-sm text-[#4A4A4A]">
            <li>· FOB Mumbai / JNPT / Cochin / Mundra</li>
            <li>· Minimum order: 5 kg saffron, 100 kg honey, 100 kg spices</li>
            <li>· Private-label packaging available</li>
            <li>· DGFT, FSSAI, Spices Board export cleared</li>
          </ul>
        </div>

        <div className="md:col-span-7">
          {submitted ? (
            <div data-testid="wholesale-success" className="bg-[#F5F2EA] border border-[#4A5D23] p-12 text-center">
              <CheckCircle2 size={48} className="mx-auto text-[#4A5D23]" />
              <h2 className="font-serif-display text-4xl mt-6 tracking-tight">Inquiry received.</h2>
              <p className="text-[#4A4A4A] mt-4">Our export team will reach out within 24 hours.</p>
              <button data-testid="wholesale-submit-another" onClick={() => setSubmitted(false)} className="mt-6 text-xs uppercase tracking-overline underline text-[#C84B31]">Submit another</button>
            </div>
          ) : (
            <form onSubmit={submit} data-testid="wholesale-form" className="bg-[#F5F2EA] border border-[#D5D1C5] p-8 md:p-12 grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Company *" testid="wf-company"><input required value={form.company_name} onChange={(e)=>update("company_name", e.target.value)} className={inputCls}/></Field>
              <Field label="Contact name *" testid="wf-contact"><input required value={form.contact_name} onChange={(e)=>update("contact_name", e.target.value)} className={inputCls}/></Field>
              <Field label="Email *" testid="wf-email"><input required type="email" value={form.email} onChange={(e)=>update("email", e.target.value)} className={inputCls}/></Field>
              <Field label="Phone" testid="wf-phone"><input value={form.phone} onChange={(e)=>update("phone", e.target.value)} className={inputCls}/></Field>
              <Field label="Country *" testid="wf-country"><input required value={form.country} onChange={(e)=>update("country", e.target.value)} className={inputCls}/></Field>
              <Field label="Port of discharge" testid="wf-port"><input value={form.port_of_discharge} onChange={(e)=>update("port_of_discharge", e.target.value)} className={inputCls}/></Field>
              <Field label="Product interest *" testid="wf-interest">
                <select value={form.product_interest} onChange={(e)=>update("product_interest", e.target.value)} className={inputCls}>
                  <option value="saffron">Saffron</option>
                  <option value="honey">Honey</option>
                  <option value="spices">Spices</option>
                  <option value="mixed">Mixed / Multiple</option>
                </select>
              </Field>
              <Field label="Estimated qty (kg) *" testid="wf-qty"><input required type="number" min="1" step="0.5" value={form.estimated_quantity_kg} onChange={(e)=>update("estimated_quantity_kg", e.target.value)} className={inputCls}/></Field>
              <Field label="Target price (USD / kg)" testid="wf-target"><input type="number" min="0" step="0.5" value={form.target_price_usd_kg} onChange={(e)=>update("target_price_usd_kg", e.target.value)} className={inputCls}/></Field>
              <Field label="Incoterm" testid="wf-incoterm">
                <select value={form.incoterm} onChange={(e)=>update("incoterm", e.target.value)} className={inputCls}>
                  <option>FOB</option><option>CIF</option><option>EXW</option><option>DAP</option>
                </select>
              </Field>
              <div className="md:col-span-2">
                <Field label="Message" testid="wf-msg"><textarea rows={4} value={form.message} onChange={(e)=>update("message", e.target.value)} className={inputCls}/></Field>
              </div>
              {err && <p data-testid="wf-error" className="md:col-span-2 text-[#C84B31] text-sm">{String(err)}</p>}
              <button data-testid="wf-submit" disabled={loading} className="md:col-span-2 bg-[#1A1A1A] text-[#FAF9F6] px-6 py-4 hover:bg-[#C84B31] transition-colors uppercase tracking-overline text-sm flex items-center justify-center gap-3 disabled:opacity-60">
                {loading ? "Submitting…" : (<>Send wholesale inquiry <Send size={16}/></>)}
              </button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

const inputCls = "w-full bg-[#FAF9F6] border border-[#D5D1C5] px-4 py-3 focus:border-[#1A1A1A] outline-none text-sm";

function Field({ label, children, testid }) {
  return (
    <label data-testid={testid} className="block">
      <span className="text-xs uppercase tracking-overline text-[#4A4A4A] block mb-2">{label}</span>
      {children}
    </label>
  );
}
